// Page views: main menu, domain overview, unit page.

import { h, confirmModal, toast } from './h.js';
import { curriculum, findDomain, findUnit } from '../core/curriculum.js';
import { LabRuntime } from '../core/runtime.js';
import { createWorkspace } from './workspace.js';
import { journeyStrip } from './missions.js';
import { homeView, careerView } from './learner-pages.js';
import { operatorStats } from '../map/mapState.js';
import { missionKind } from '../map/mapData.js';
import { hintCost, xpWithHints, baseXp, unitXp } from '../core/xp.js';
import { logoMark, wordmark, BRAND } from './brand.js';
import { icon } from './icons.js';
import { prefersReducedMotion as reduceMotion } from './carousel.js';

/** Scroll to a landing-page section; from another page, go to the landing page first. */
export function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el && ['', '#', '#/'].includes(location.hash)) { el.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' }); return; }
  window.__rzScrollTo = id;
  location.hash = '#/';
}

const STATE_LABEL = {
  complete: 'COMPLETE', available: 'AVAILABLE', 'in-progress': 'IN PROGRESS', locked: 'LOCKED', pending: 'IMPLEMENTATION PENDING',
};
const ICONS = { complete: '✓', available: '▶', 'in-progress': '◐', locked: '🔒', pending: '⋯' };

export function topbar(crumbs = [], ctx = {}) {
  const u = ctx.session?.user;
  const admin = u?.role === 'admin';
  const stats = u && !admin && ctx.progress ? operatorStats(ctx.rewardProgress || ctx.progress, ctx.curriculum) : null;
  // Support Desk, domains, labs and the map all belong to MISSIONS; Career is part of PROFILE.
  const active = ['map', 'missions', 'support', 'domain', 'unit'].includes(ctx.active) ? 'missions' : ['career', 'security', 'profile'].includes(ctx.active) ? 'profile' : ['', 'home'].includes(ctx.active) ? '' : ctx.active;
  const showCrumbs = admin || !['', 'home', 'map', 'missions', 'career', 'support', 'security', 'profile'].includes(ctx.active);
  const navLink = (key, href, testid, label) => h('a', { class: active === key ? 'active' : '', href, 'data-testid': testid, 'aria-current': active === key ? 'page' : null }, label);
  // Public visitors: Home · Learning Path · Labs · How it works · Pricing — every link goes to a real
  // page (#/, #/support) or a real landing-page section; nothing is a dead end.
  const onLanding = !u && ['', 'home'].includes(ctx.active);
  const header = h('header', { class: `topbar ${u ? 'is-app' : 'is-public'} ${admin ? 'is-admin' : ''}` });
  const closeMenu = () => { header.classList.remove('menu-open'); menuBtn?.setAttribute('aria-expanded', 'false'); };
  const scrollLink = (label, target, testid) => h('a', { href: '#/', 'data-scroll': target, 'data-testid': testid, onclick: (e) => { e.preventDefault(); closeMenu(); scrollToSection(target); } }, label);
  const menuBtn = !u ? h('button', { class: 'nav-menu-btn', type: 'button', 'aria-label': 'Open menu', 'aria-expanded': 'false', 'aria-controls': 'rz-public-nav', 'data-testid': 'nav-menu-toggle', onclick: () => {
    const open = !header.classList.contains('menu-open');
    header.classList.toggle('menu-open', open); menuBtn.setAttribute('aria-expanded', String(open)); menuBtn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
  } }, icon('menu', { size: 22, cls: 'when-closed' }), icon('close', { size: 22, cls: 'when-open' })) : null;
  header.addEventListener('keydown', (e) => { if (e.key === 'Escape' && header.classList.contains('menu-open')) { closeMenu(); menuBtn?.focus(); } });
  header.append(...[
    h('a', { class: 'brand', href: '#/', 'aria-label': 'RouteZero home' },
      logoMark({ size: 34, cls: 'brand-logo' }),
      h('span', { class: 'brand-copy' }, h('b', {}, wordmark()), u ? h('small', {}, BRAND.tagline) : null)),
    !u ? h('nav', { class: 'primary-nav public-nav', id: 'rz-public-nav', 'aria-label': 'Primary navigation', 'data-testid': 'public-nav' },
      h('a', { href: '#/', class: onLanding ? 'active' : '', 'aria-current': onLanding ? 'page' : null, 'data-testid': 'nav-home', onclick: (e) => { closeMenu(); if (onLanding) { e.preventDefault(); scrollTo({ top: 0, behavior: reduceMotion() ? 'auto' : 'smooth' }); } } }, 'Home'),
      scrollLink('Learning Path', 'rz-paths', 'nav-paths'),
      h('a', { href: '#/support', class: ctx.active === 'support' ? 'active' : '', 'aria-current': ctx.active === 'support' ? 'page' : null, 'data-testid': 'nav-labs', onclick: closeMenu }, 'Labs'),
      scrollLink('How it Works', 'rz-how', 'nav-how'),
      scrollLink('Pricing', 'rz-pricing', 'nav-pricing'),
      h('a', { href: '#/login', class: 'nav-menu-only', 'data-testid': 'nav-menu-signin', onclick: closeMenu }, 'Sign In')) : null,
  ].filter(Boolean));
  if (!u) {
    if (ctx.active !== 'login' && ctx.active !== 'signup') header.append(h('div', { class: 'public-actions', 'data-testid': 'public-actions' },
      h('a', { class: 'btn ghost small nav-signin', href: '#/login', 'data-testid': 'nav-login' }, 'Sign In'),
      h('a', { class: 'btn primary small create-account', href: '#/signup', 'data-testid': 'nav-create-account' }, 'Get Started', icon('arrowRight', { size: 16 }))));
    header.append(menuBtn);
    return header;
  }
  return h('header', { class: `topbar is-app ${admin ? 'is-admin' : ''}` },
    h('a', { class: 'brand', href: '#/' },
      logoMark({ size: 34, cls: 'brand-logo' }),
      h('span', { class: 'brand-copy' }, h('b', {}, wordmark()), h('small', {}, BRAND.tagline))),
    u && !admin ? h('nav', { class: 'primary-nav', 'aria-label': 'Primary navigation' },
      navLink('', '#/', 'main-menu', 'HOME'),
      navLink('missions', '#/missions', 'nav-missions', 'MISSIONS'),
      navLink('profile', '#/profile', 'nav-profile-tab', 'PROFILE')) : null,
    showCrumbs && crumbs.length ? h('nav', { class: 'crumbs', 'aria-label': 'Breadcrumb' }, crumbs.map((c, i) => [i ? h('span', { class: 'sep' }, '/') : null, c.href ? h('a', { href: c.href }, c.label) : h('span', {}, c.label)])) : null,
    !u && ctx.active !== 'login' && ctx.active !== 'signup' ? h('div', { class: 'public-actions', 'data-testid': 'public-actions' },
      h('a', { class: 'btn on-dark-ghost small create-account', href: '#/signup', 'data-testid': 'nav-create-account' }, 'CREATE ACCOUNT'),
      h('a', { class: 'btn primary small', href: '#/login', 'data-testid': 'nav-login' }, 'LOG IN')) : null,
    u ? h('div', { class: 'who', 'data-testid': 'nav-who' },
      admin ? h('a', { class: 'chip admin-chip', href: '#/admin', 'data-testid': 'admin-link' }, 'ADMIN TEST MODE') : null,
      stats ? h('span', { class: 'nav-xp', 'data-testid': 'nav-xp' }, `LV ${String(ctx.supportSummary ? 1 + Math.floor((stats.xp + ctx.supportSummary.done * 100) / 500) : stats.level).padStart(2, '0')} · ${(stats.xp + (ctx.supportSummary?.done || 0) * 100).toLocaleString()} XP`) : null,
      !admin ? h('a', { class: 'profile-link', href: '#/profile', 'data-testid': 'nav-profile', 'aria-label': `Profile: ${u.name}` }, h('span', { class: `profile-avatar rz-frame rz-frame-${ctx.look?.frame || 'none'}`, 'aria-hidden': 'true', 'data-testid': 'nav-avatar' }, u.name.trim().split(/\s+/).slice(0, 2).map((x) => x[0]).join('').toUpperCase()), h('span', { class: 'profile-label' }, 'PROFILE')) : h('span', { class: 'who-name', title: `Signed in as ${u.name} (${u.role})`, 'data-testid': 'who' }, u.name),
      h('button', { class: 'btn ghost small signout-btn', 'data-testid': 'sign-out', onclick: async () => {
        if (admin) { await ctx.onLogout?.(); return; }
        if (await confirmModal({ title: 'Sign out of RouteZero?', body: '<p>Your progress stays saved. You can sign back in any time.</p>', confirm: 'SIGN OUT', cancel: 'CANCEL' })) await ctx.onLogout?.();
      } }, admin ? 'Exit Admin Mode' : 'SIGN OUT')) : null);
}

function bar(pct, cls = '') {
  return h('div', { class: `bar ${cls}`, role: 'progressbar', 'aria-valuenow': pct, 'aria-valuemin': 0, 'aria-valuemax': 100 }, h('span', { style: { width: `${pct}%` } }));
}

// ======================================================================== HOME
export function menuView(progress, user = null, support = null, opts = {}) { return homeView(progress, user, curriculum, support, opts); }

// ======================================================================== DOMAIN
export function domainView(progress, dId, rerender) {
  const d = findDomain(dId);
  const s = progress.domainSummary(dId);
  if (!s.unlocked) return lockedPage(`Domain ${d.number} is locked`, `Complete Domain ${String(+d.number - 1).padStart(2, '0')} to unlock ${d.title}.`);
  const next = progress.currentUnit(dId);
  return h('main', { class: 'page domain', 'data-reading': 'article' },
    h('section', { class: 'domain-head panel panel-dark' },
      h('div', {},
        h('div', { class: 'eyebrow' }, `DOMAIN ${d.number}`),
        h('h1', {}, d.title),
        h('p', { class: 'lede' }, d.tagline),
        h('div', { class: 'dh-journey' }, h('div', { class: 'mj-head' }, h('span', {}, 'MISSION JOURNEY'), h('span', { class: 'mj-count' }, `${s.done}/${s.total} units`)), journeyStrip(progress, d, { links: true }))),
      h('div', { class: 'dh-side' },
        h('div', { class: 'stat' }, h('b', {}, `${s.done}/${s.total}`), h('span', {}, 'units complete')),
        bar(s.pct),
        s.complete ? h('div', { class: 'ok-text' }, '✓ Domain complete') : next ? h('a', { class: 'btn primary', href: `#/unit/${dId}/${next.id}`, 'data-testid': 'domain-continue' }, `${progress.unitState(dId, next.id) === 'in-progress' ? 'Resume' : 'Start'} ${next.id.replace('u', 'Unit ')}`) : null,
        h('button', { class: 'btn danger ghost small', 'data-testid': 'reset-domain', onclick: async () => {
          if (await confirmModal({ title: `Reset Domain ${d.number}?`, body: `<p>This clears progress and lab state for every unit in <b>${d.title}</b> only.</p><p>Other domains are not affected.</p>`, confirm: 'Reset this domain', danger: true })) {
            progress.resetDomain(dId); toast(`Domain ${d.number} reset`); rerender();
          }
        } }, '↺ RESET THIS DOMAIN'))),
    h('ol', { class: 'unit-list' },
      d.units.map((u, i) => {
        const st = progress.unitState(dId, u.id);
        const open = progress.canOpen(dId, u.id);
        const isFinal = /final/i.test(u.type);
        return h('li', { class: `unit-row ${st} ${isFinal ? 'final' : ''}` },
          h(open ? 'a' : 'div', { class: 'unit-link', href: open ? `#/unit/${dId}/${u.id}` : null, 'data-testid': `unit-${u.id}`, 'data-state': st },
            h('span', { class: `u-icon ${st}` }, ICONS[st]),
            h('span', { class: 'u-num' }, String(i + 1).padStart(2, '0')),
            h('span', { class: 'u-main' }, h('span', { class: 'u-title' }, u.title), h('span', { class: 'u-type' }, u.kind ? h('span', { class: `u-kind ${u.kind.toLowerCase()}` }, u.kind) : null, u.type)),
            h('span', { class: `chip ${st}` }, STATE_LABEL[st])));
      })));
}

function lockedPage(title, msg) {
  return h('main', { class: 'page' }, h('section', { class: 'panel locked-page' }, h('h1', {}, '🔒 ', title), h('p', { class: 'muted' }, msg), h('a', { class: 'btn primary', href: '#/' }, 'Back to Main Menu')));
}

// ======================================================================== UNIT
export function unitView(progress, dId, uId, rerender, opts = {}) {
  const d = findDomain(dId);
  const u = findUnit(dId, uId);
  if (!u) return lockedPage('Unit not found', 'This learning unit does not exist.');
  const st0 = progress.unitState(dId, uId);
  if (st0 === 'pending') return lockedPage('IMPLEMENTATION PENDING', `“${u.title}” has not been built yet. It cannot be opened or completed, and it blocks the rest of this domain.`);
  if (!progress.canOpen(dId, uId)) return lockedPage(`${u.title} is locked`, 'Complete the previous learning unit first. Units must be completed in order.');

  const rt = new LabRuntime(dId, u, progress).init();
  if (opts.admin && typeof window !== 'undefined') delete window.__NELL_FAULTS__; // forced faults apply to one launch only
  const idx = d.units.findIndex((x) => x.id === uId);
  const nextU = d.units[idx + 1];

  const stageHost = h('div', { class: 'stage-host' });
  const rail = h('aside', { class: 'rail' });
  const flow = h('ol', { class: 'flow' });
  const statusChip = h('span', { class: 'chip' });
  const completeHost = h('div');
  const adminHost = h('div', { class: 'admin-answer-host' }); // admin reference-answer panel (empty for learners)
  let ws = null;
  // Split lab layout (presentation only): LEFT = incident / learning column, RIGHT = workspace.
  const wsHost = h('div', { class: 'ws-host' });
  const openWs = h('button', { class: 'btn primary wide ws-open-btn', 'data-testid': 'ws-open', 'aria-label': 'Open lab workspace (network diagram and consoles)', onclick: () => setDrawer(true) }, '▶ OPEN LAB WORKSPACE');
  const left = h('div', { class: 'lab-left', 'data-testid': 'lab-left' }, opts.adminTools ? adminHost : null, h('div', { class: 'unit-main' }, stageHost), rail, h('div', { class: 'ws-open-wrap' }, openWs));
  function setDrawer(on) {
    page.classList.toggle('ws-open', on);
    if (on) { ws?.el.querySelector('.ws-view.on')?.focus({ preventScroll: true }); } else openWs.focus({ preventScroll: true });
  }

  const page = h('main', { class: 'page unit', 'data-unit': `${dId}.${uId}` },
    h('section', { class: 'unit-head panel' },
      h('div', { class: 'uh-main' },
        h('div', { class: 'eyebrow' }, `DOMAIN ${d.number} · ${/final/i.test(u.type) ? `UNIT ${u.num} · FINAL DOMAIN CHALLENGE` : `LAB ${u.num} · ${u.type.toUpperCase()}`}`),
        h('h1', {}, u.title),
        h('p', { class: 'objective' }, h('b', {}, 'OBJECTIVE  '), u.objective)),
      h('div', { class: 'uh-side' },
        statusChip,
        h('a', { class: 'btn ghost small', href: `#/domain/${dId}` }, '← Domain overview'),
        h('a', { class: 'btn ghost small', href: `#/map/${dId}/site/${uId}`, 'data-testid': 'unit-map' }, '🗺 Show on map'),
        h('button', { class: 'btn danger ghost small', 'data-testid': 'reset-lab', onclick: async () => {
          if (await confirmModal({ title: 'Reset this lab?', body: `<p>Clears your progress, device configuration and console history for <b>${u.title}</b> only.</p><p>Every other unit keeps its status.</p>`, confirm: 'Reset this lab', danger: true })) {
            progress.resetUnit(dId, uId); toast('Lab reset'); rerender();
          }
        } }, '↺ RESET THIS LAB')),
      flow),
    // a mission marked complete whose finished run is not on this device (completed before server
    // checks, or on another computer) still shows its server status — and "Redo to verify" when needed
    !rt.state.completed && opts.verifyBadge && progress.unitState(dId, uId) === 'complete' ? h('section', { class: 'panel rz-lab-verify-top' }, opts.verifyBadge(`${dId}.${uId}`)) : null,
    // started before missions were checked on the server: no journal, so finishing it can't be verified
    !rt.state.completed && opts.verifyBadge && progress.unitState(dId, uId) !== 'complete' && !Array.isArray(rt.state.journal)
      ? h('section', { class: 'panel rz-lab-verify-top' }, h('p', { class: 'rz-verify', 'data-state': 'redo', 'data-testid': 'lab-legacy' },
        h('span', { class: 'rz-verify-text' }, 'THIS MISSION WAS STARTED BEFORE SERVER CHECKS, SO FINISHING THIS RUN CAN’T BE VERIFIED. RESTART IT TO EARN ITS XP.'),
        h('button', { class: 'btn small primary rz-verify-redo', type: 'button', 'data-testid': 'lab-legacy-restart', onclick: () => { progress.resetUnit(dId, uId); rerender(); } }, 'Restart mission')))
      : null,
    completeHost,
    h('div', { class: 'unit-grid' }, left, wsHost));

  function drawFlow() {
    flow.textContent = '';
    const cur = rt.stage?.phase;
    const reached = new Set(rt.stages.slice(0, rt.state.stage + 1).map((s) => s.phase));
    (u.flow || [...new Set(rt.stages.map((s) => s.phase)), 'COMPLETE']).forEach((p) => {
      const done = rt.state.completed || (reached.has(p) && p !== cur);
      flow.append(h('li', { class: `${p === cur && !rt.state.completed ? 'cur' : ''} ${done ? 'done' : ''}` }, p));
    });
    const s = rt.state.completed ? 'complete' : 'in-progress';
    statusChip.className = `chip ${s}`;
    statusChip.textContent = rt.state.completed ? '✓ COMPLETE' : `STAGE ${rt.state.stage + 1} OF ${rt.stages.length}`;
  }

  function drawStage() {
    stageHost.textContent = '';
    const st = rt.stage;
    const card = h('section', { class: 'stage panel', 'data-stage': st.id },
      h('div', { class: 'stage-head' }, h('span', { class: 'phase' }, st.phase), h('h2', {}, st.title)));
    if (st.ticket) card.append(ticketCard(st.ticket, rt));
    const content = typeof st.content === 'function' ? st.content(rt) : st.content;
    if (content) card.append(h('div', { class: 'prose', html: content }));
    for (const w of st.widgets || []) {
      const box = h('div', { class: `widget ${rt.widget(w.id).done ? 'done' : ''}`, 'data-widget': w.id });
      card.append(box);
      w.mount(box, rt, { done: () => { rt.widgetDone(w.id); box.classList.add('done'); drawRail(); }, state: rt.widget(w.id), save: () => rt.save(), refresh: () => { ws?.refresh(); drawRail(); } });
    }
    stageHost.append(card);
    const split = st.workspace !== false && !!u.network;
    page.classList.toggle('split', split);
    if (!split) { page.classList.remove('ws-expanded', 'ws-open'); wsHost.textContent = ''; }
    if (split) {
      if (!ws) ws = createWorkspace(rt, { onChange: () => drawRail(), onExpand: (on) => page.classList.toggle('ws-expanded', on), onClose: () => setDrawer(false) });
      ws.setDevices();
      if (ws.el.parentNode !== wsHost) wsHost.append(ws.el);
    }
    if (st.after) stageHost.append(h('div', { class: 'prose panel after', html: typeof st.after === 'function' ? st.after(rt) : st.after }));
  }

  function drawRail() {
    rail.textContent = '';
    const st = rt.stage;
    if (opts.adminTools) rail.append(opts.adminTools(rt, { redraw: () => { drawFlow(); drawStage(); drawRail(); drawComplete(); }, rerender, answerHost: adminHost }));
    if (rt.state.completed) {
      rail.append(h('section', { class: 'panel rail-card ok' }, h('div', { class: 'rc-title' }, 'COMPLETION'), h('p', {}, 'This unit is complete and stays complete. You can revisit it any time, or reset just this lab to practise again.')));
      return;
    }
    const ev = rt.evidenceStatus();
    if (ev.length) {
      rail.append(h('section', { class: 'panel rail-card', 'data-testid': 'evidence' },
        h('div', { class: 'rc-title' }, 'REQUIRED EVIDENCE', h('span', { class: 'count' }, `${ev.filter((e) => e.done).length}/${ev.length}`)),
        h('ul', { class: 'checks' }, ev.map((e) => h('li', { class: e.done ? 'done' : '' }, h('i'), e.text)))));
    }
    if (st.tasks?.length) {
      const vr = rt.state.verify[st.id];
      rail.append(h('section', { class: 'panel rail-card' },
        h('div', { class: 'rc-title' }, 'VERIFICATION TARGETS'),
        h('ul', { class: 'checks targets' }, st.tasks.map((t) => {
          const r = vr?.results.find((x) => x.id === t.id);
          return h('li', { class: r ? (r.ok ? 'done' : 'fail') : '' }, h('i'), t.text, r && !r.ok && r.fail ? h('div', { class: 'fail-msg' }, r.fail) : null);
        })),
        h('div', { class: 'muted small' }, 'Targets are checked against live device state only when you press Verify.')));
    }
    if (st.question) rail.append(h('section', { class: 'panel rail-card q' }, h('div', { class: 'rc-title' }, 'ENGINEER QUESTION'), h('p', { html: st.question })));
    // Coach
    const hints = st.hints || [];
    const shown = rt.hintsShown();
    const coach = h('section', { class: 'panel rail-card coach' }, h('div', { class: 'rc-title' }, 'LAB COACH'));
    if (st.coach) coach.append(h('p', { class: 'small', html: st.coach }));
    hints.slice(0, shown).forEach((t, i) => coach.append(h('div', { class: 'hint' }, h('b', {}, `Hint ${i + 1}  `), h('span', { html: t }))));
    // hints cost XP (src/core/xp.js): say so on the button, and what the mission is worth now
    const kind = missionKind(u);
    const used = Object.values(rt.state.hints || {}).reduce((a, b) => a + b, 0);
    if (used) coach.append(h('p', { class: 'small muted', 'data-testid': 'hint-xp' }, `${used} hint${used === 1 ? '' : 's'} used · this mission now earns ${xpWithHints(kind, used)} of ${baseXp(kind)} XP`));
    if (shown < hints.length) coach.append(h('button', { class: 'btn ghost small', 'data-testid': 'hint', 'aria-label': `Ask the coach for hint ${shown + 1} of ${hints.length}; costs ${hintCost(kind)} XP`, onclick: () => { rt.revealHint(); drawRail(); } }, `Ask the coach (hint ${shown + 1} of ${hints.length} · −${hintCost(kind)} XP)`));
    if (hints.length || st.coach) rail.append(coach);
    // Verify
    const b = rt.blockers();
    const last = lastVerify;
    const box = h('section', { class: 'panel rail-card verify' }, h('div', { class: 'rc-title' }, 'VERIFICATION'));
    if (last?.blocked) box.append(h('div', { class: 'vmsg warn' }, 'Not yet — collect the required evidence and finish the activity first.'));
    else if (last && !last.ok) box.append(h('div', { class: 'vmsg bad', 'data-testid': 'verify-fail' }, 'Verification failed. Observed behaviour still differs from expected. Keep investigating.'));
    const isLast = rt.state.stage === rt.stages.length - 1;
    box.append(h('button', {
      class: `btn primary wide ${b.length ? 'soft' : ''}`, 'data-testid': 'verify',
      onclick: () => { lastVerify = rt.verify(); if (!lastVerify.ok) drawRail(); },
    }, st.verifyLabel || (isLast ? (u.type === 'Lesson' ? 'Complete Lesson' : 'Verify & complete unit') : (st.tasks?.length || ev.length ? 'Verify & continue' : 'Continue'))));
    if (b.length) box.append(h('div', { class: 'muted small' }, `${b.length} requirement${b.length > 1 ? 's' : ''} outstanding.`));
    rail.append(box);
  }
  let lastVerify = null;

  function drawComplete() {
    completeHost.textContent = '';
    if (!rt.state.completed) return;
    const nextState = nextU ? progress.unitState(dId, nextU.id) : null;
    const domainDone = progress.isDomainComplete(dId);
    completeHost.append(h('section', { class: 'panel completion', 'data-testid': 'completion' },
      h('div', { class: 'big-ok' }, '✓'),
      h('div', {},
        h('h2', {}, `${/final/i.test(u.type) ? 'Challenge' : 'Unit'} complete${rt.wasBypassed() ? ' (admin test)' : ''}`),
        rt.wasBypassed() ? null : (() => { const r = { status: 'complete', ...(progress.unitRecord(dId, u.id) || {}) }; const n = Number(r.hints) || 0; return h('p', { class: 'ok-text', 'data-testid': 'completion-xp' }, `+${unitXp(missionKind(u), r)} XP${n ? ` · ${n} hint${n === 1 ? '' : 's'} used` : ' · no hints used'}`); })(),
        rt.wasBypassed() ? h('p', { class: 'bypass-note', 'data-testid': 'bypass-warning', role: 'status' }, 'Admin test bypass — learner validation was not performed. This completion exists in the admin test sandbox only and does not mean the lab was technically validated.') : null,
        !rt.wasBypassed() && opts.verifyBadge ? opts.verifyBadge(`${dId}.${u.id}`) : null,
        u.review ? h('div', { class: 'prose', html: u.review(rt) }) : null,
        domainDone ? h('p', { class: 'ok-text' }, `Domain ${d.number} complete. ${curriculum.domains[curriculum.domains.indexOf(d) + 1] ? 'The next domain is unlocked.' : ''}`) : null,
        h('div', { class: 'row' },
          nextU && progress.canOpen(dId, nextU.id)
            ? h('a', { class: 'btn primary', href: `#/unit/${dId}/${nextU.id}`, 'data-testid': 'next-unit' }, `NEXT UNIT → ${nextU.title}`)
            : nextU ? h('span', { class: 'chip pending' }, `Next: ${nextU.title} — ${nextState === 'pending' ? 'IMPLEMENTATION PENDING' : 'locked'}`) : null,
          h('a', { class: 'btn ghost', href: `#/map/${dId}/site`, 'data-testid': 'return-map' }, '🗺 Return to map'),
          h('a', { class: 'btn ghost', href: `#/domain/${dId}` }, 'Domain overview'),
          h('a', { class: 'btn ghost', href: '#/' }, 'Main Menu')))));
  }

  rt.on((type) => {
    if (type === 'event' || type === 'evidence' || type === 'widget') { if (lastVerify?.blocked && type !== 'event') lastVerify = null; drawRail(); if (type === 'event') ws?.drawDiagram(); }
    if (type === 'stage') { lastVerify = null; drawFlow(); drawStage(); drawRail(); left.scrollTop = 0; window.scrollTo({ top: 0, behavior: 'smooth' }); }
    if (type === 'complete') { drawFlow(); drawRail(); drawComplete(); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  });

  // Measure where the split starts so it can fill exactly the rest of the viewport.
  const grid = page.querySelector('.unit-grid');
  const fitSplit = () => {
    if (!page.isConnected) return;
    const top = grid.getBoundingClientRect().top + window.scrollY;
    page.style.setProperty('--split-top', `${Math.round(top)}px`);
  };
  if (typeof window !== 'undefined') {
    const onResize = () => { if (!page.isConnected) { window.removeEventListener('resize', onResize); return; } fitSplit(); };
    window.addEventListener('resize', onResize);
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => { if (!page.isConnected) { ro.disconnect(); return; } fitSplit(); });
      for (const x of [page.querySelector('.unit-head'), completeHost]) if (x) ro.observe(x);
      setTimeout(() => { fitSplit(); for (const x of [document.querySelector('.admin-banner'), document.querySelector('.topbar')]) if (x) ro.observe(x); }, 0);
    }
  }
  drawFlow(); drawStage(); drawRail(); drawComplete();
  page.__rt = rt;
  return page;
}

export function ticketCard(t, rt) {
  const tk = typeof t === 'function' ? t(rt) : t;
  return h('div', { class: 'ticket', 'data-testid': 'ticket' },
    h('div', { class: 'tk-head' },
      h('span', { class: 'tk-id' }, `TICKET #${tk.id}`),
      h('span', { class: `tk-pri p${tk.priority || 3}` }, `P${tk.priority || 3}`),
      h('span', { class: 'tk-meta' }, `${tk.from} · ${tk.site || 'BRANCH-01'} · opened ${tk.opened || '09:12'}`)),
    h('div', { class: 'tk-subj' }, tk.subject),
    h('blockquote', {}, tk.body),
    tk.extra ? h('div', { class: 'tk-extra', html: tk.extra }) : null);
}
